import { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import { useAuth } from '../context/AuthContext'
import { useTheme } from '../context/ThemeContext'
import { useCategories } from '../context/CategoriesContext'
import { useLanguage } from '../context/LanguageContext'
import { COLOR_OPTIONS } from '../lib/utils'
import { MEJORA_ICON_OPTIONS, FINANCE_ICON_OPTIONS, IDEA_ICON_OPTIONS, getIconLabel } from '../lib/iconOptions'
import { Button } from '../components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Switch } from '../components/ui/switch'
import { Badge } from '../components/ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'
import { ScrollArea } from '../components/ui/scroll-area'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog'
import { toast } from 'sonner'
// Reusable modal components for i18n
import { FinanceCategoryModal, FinanceSubcategoryModal } from '../components/modals/FinanceCategoryModal'
import { MejoraCategoryModal, MejoraSubcategoryModal } from '../components/modals/MejoraCategoryModal'
import { IdeaBucketModal } from '../components/modals/IdeaBucketModal'
import { 
  Settings, Target, Moon, Sun, LogOut, User, Plus, Edit, Trash2, 
  Archive, RotateCcw, GripVertical, ChevronUp, ChevronDown, AlertTriangle,
  Dumbbell, Brain, Laptop, CandlestickChart, TrendingUp, Building2, BookOpen,
  Heart, Star, Zap, Coffee, Music, Palette, Briefcase, GraduationCap,
  Users, Home, Car, Plane, Circle, FolderOpen, Wallet, ArrowUpCircle, ArrowDownCircle,
  Utensils, Shirt, CreditCard, BadgeDollarSign, PiggyBank, Gift, Gamepad2,
  Layers, ChevronRight, ToggleLeft, ToggleRight, DollarSign, Bell, BellOff,
  Globe, Check, Lightbulb, Sparkles, Rocket, MoreHorizontal,
  // New Mejora icons
  Activity, Moon as MoonIcon, Apple, Shield, Clock, BookMarked, PenTool, 
  PencilLine, TreeDeciduous, UsersRound, UserPlus, Flame, ListChecks, 
  CalendarDays, RotateCw, MessageCircle, Scale,
  // New Finance icons  
  ShoppingCart, UtensilsCrossed, Bus, Fuel, ShieldCheck, RefreshCw, Wifi,
  Smartphone, Palmtree, FileText, CircleDollarSign, Landmark, HeartHandshake,
  PawPrint, Baby, Wrench, Beer, Pill, Scissors, Tv, Droplets, Phone,
  // New Idea icons
  FolderKanban, Package, Wand2, Search, Megaphone, BadgeCheck, Cog, Bot,
  FlaskConical, Network, Eye, Trophy, Compass, Map, GitBranch, Blocks
} from 'lucide-react'

const API = `${process.env.REACT_APP_BACKEND_URL}/api`

// Extended icon map with all icons
const iconMap = {
  // Original icons
  Dumbbell, Brain, Laptop, CandlestickChart, TrendingUp, Building2, BookOpen,
  Heart, Star, Target, Zap, Coffee, Music, Palette, Briefcase, GraduationCap,
  Users, Home, Car, Plane, Circle, Wallet, Utensils, Shirt, CreditCard,
  BadgeDollarSign, PiggyBank, Gift, Gamepad2, Lightbulb, Sparkles, Rocket, MoreHorizontal, User,
  // New Mejora icons
  Activity, Moon: MoonIcon, Apple, Shield, Clock, BookMarked, PenTool,
  PencilLine, TreeDeciduous, UsersRound, UserPlus, Flame, ListChecks,
  CalendarDays, RotateCw, MessageCircle, Scale,
  // New Finance icons
  ShoppingCart, UtensilsCrossed, Bus, Fuel, ShieldCheck, RefreshCw, Wifi,
  Smartphone, Palmtree, FileText, CircleDollarSign, Landmark, HeartHandshake,
  PawPrint, Baby, Wrench, Beer, Pill, Scissors, Tv, Droplets, Phone,
  // New Idea icons
  FolderKanban, Package, Wand2, Search, Megaphone, BadgeCheck, Cog, Bot,
  FlaskConical, Network, Eye, Trophy, Compass, Map, GitBranch, Blocks
}

export default function SettingsPage() {
  const { user, updateSettings, logout } = useAuth()
  const { theme, setThemeMode } = useTheme()
  const { t, locale, setLocale } = useLanguage()
  const { 
    activeCategories, archivedCategories, addCategory, updateCategory, 
    reorderCategories, deleteCategory, restoreCategory, fetchCategories 
  } = useCategories()
  
  const [dailyGoal, setDailyGoal] = useState(user?.daily_goal || 20)
  const [saving, setSaving] = useState(false)
  const [savingCategory, setSavingCategory] = useState(false)
  const [savingFinanceCategory, setSavingFinanceCategory] = useState(false)
  const [showCategoryModal, setShowCategoryModal] = useState(false)
  const [editingCategory, setEditingCategory] = useState(null)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [deletingCategory, setDeletingCategory] = useState(null)
  const [deleteAction, setDeleteAction] = useState('archive')
  const [reassignTo, setReassignTo] = useState('')
  const [showArchived, setShowArchived] = useState(false)
  
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    icon: 'Circle',
    color: '#6b7280'
  })

  // Finance Categories State
  const [financeCategories, setFinanceCategories] = useState([])
  const [loadingFinance, setLoadingFinance] = useState(true)
  const [showFinanceCategoryModal, setShowFinanceCategoryModal] = useState(false)
  const [editingFinanceCategory, setEditingFinanceCategory] = useState(null)
  const [financeCategoryForm, setFinanceCategoryForm] = useState({
    name: '',
    icon: 'Circle',
    color: '#6b7280',
    category_type: 'expense'
  })
  const [financeFilter, setFinanceFilter] = useState('all') // 'all', 'income', 'expense'
  const [showDeleteFinanceDialog, setShowDeleteFinanceDialog] = useState(false)
  const [deletingFinanceCategory, setDeletingFinanceCategory] = useState(null)

  // Finance Subcategories State
  const [financeSubcategories, setFinanceSubcategories] = useState([])
  const [showFinanceSubcategoriesModal, setShowFinanceSubcategoriesModal] = useState(false)
  const [selectedFinanceCategory, setSelectedFinanceCategory] = useState(null)
  const [showFinanceSubcategoryForm, setShowFinanceSubcategoryForm] = useState(false)
  const [editingFinanceSubcategory, setEditingFinanceSubcategory] = useState(null)
  const [financeSubcategoryForm, setFinanceSubcategoryForm] = useState({
    name: ''
  })

  // Idea Buckets State
  const [ideaBuckets, setIdeaBuckets] = useState([])
  const [loadingBuckets, setLoadingBuckets] = useState(true)
  const [showBucketModal, setShowBucketModal] = useState(false)
  const [editingBucket, setEditingBucket] = useState(null)
  const [bucketForm, setBucketForm] = useState({
    name: '',
    icon: 'Circle'
  })
  const [savingBucket, setSavingBucket] = useState(false)
  const [showDeleteBucketDialog, setShowDeleteBucketDialog] = useState(false)
  const [deletingBucket, setDeletingBucket] = useState(null)

  // Fetch idea buckets
  const fetchIdeaBuckets = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/idea-buckets?include_inactive=true`)
      setIdeaBuckets(response.data)
    } catch (error) {
      console.error('Error fetching idea buckets:', error)
    } finally {
      setLoadingBuckets(false)
    }
  }, [])

  useEffect(() => {
    fetchIdeaBuckets()
  }, [fetchIdeaBuckets])

  // Idea bucket handlers
  const openNewBucket = () => {
    setEditingBucket(null)
    setBucketForm({ name: '', icon: 'Circle' })
    setShowBucketModal(true)
  }

  const openEditBucket = (bucket) => {
    setEditingBucket(bucket)
    setBucketForm({
      name: bucket.name,
      icon: bucket.icon || 'Circle'
    })
    setShowBucketModal(true)
  }

  const handleSaveBucket = async () => {
    if (!bucketForm.name.trim()) {
      toast.error(locale === 'es-MX' ? 'El nombre es obligatorio' : 'Name is required')
      return
    }

    if (savingBucket) return
    setSavingBucket(true)

    try {
      if (editingBucket) {
        await axios.put(`${API}/idea-buckets/${editingBucket.id}`, bucketForm)
        toast.success(t('settings.bucketUpdated'))
      } else {
        await axios.post(`${API}/idea-buckets`, bucketForm)
        toast.success(t('settings.bucketCreated'))
      }
      setShowBucketModal(false)
      fetchIdeaBuckets()
    } catch (error) {
      const message = error.response?.data?.detail || (locale === 'es-MX' ? 'Error al guardar' : 'Error saving')
      toast.error(message)
    } finally {
      setSavingBucket(false)
    }
  }

  const openDeleteBucket = (bucket) => {
    setDeletingBucket(bucket)
    setShowDeleteBucketDialog(true)
  }

  const handleDeleteBucket = async () => {
    if (!deletingBucket) return

    try {
      await axios.delete(`${API}/idea-buckets/${deletingBucket.id}`)
      toast.success(t('settings.bucketDeleted'))
      setShowDeleteBucketDialog(false)
      fetchIdeaBuckets()
    } catch (error) {
      toast.error(locale === 'es-MX' ? 'Error al eliminar' : 'Error deleting')
    }
  }

  // Fetch finance categories
  const fetchFinanceCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/finance/categories`)
      setFinanceCategories(response.data)
    } catch (error) {
      console.error('Error fetching finance categories:', error)
    } finally {
      setLoadingFinance(false)
    }
  }, [])

  useEffect(() => {
    fetchFinanceCategories()
  }, [fetchFinanceCategories])

  // Finance category handlers
  const openNewFinanceCategory = () => {
    setEditingFinanceCategory(null)
    setFinanceCategoryForm({ name: '', icon: 'Circle', color: '#6b7280', category_type: 'expense' })
    setShowFinanceCategoryModal(true)
  }

  const openEditFinanceCategory = (category) => {
    setEditingFinanceCategory(category)
    setFinanceCategoryForm({
      name: category.name,
      icon: category.icon,
      color: category.color,
      category_type: category.category_type
    })
    setShowFinanceCategoryModal(true)
  }

  const handleSaveFinanceCategory = async () => {
    if (!financeCategoryForm.name.trim()) {
      toast.error('El nombre es obligatorio')
      return
    }

    if (savingFinanceCategory) return // Prevent double submit
    setSavingFinanceCategory(true)

    try {
      if (editingFinanceCategory) {
        await axios.put(`${API}/finance/categories/${editingFinanceCategory.id}`, financeCategoryForm)
        toast.success('Categoría actualizada')
      } else {
        await axios.post(`${API}/finance/categories`, financeCategoryForm)
        toast.success('Categoría creada')
      }
      setShowFinanceCategoryModal(false)
      fetchFinanceCategories()
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al guardar la categoría'
      toast.error(message)
      console.error('Error saving finance category:', error)
    } finally {
      setSavingFinanceCategory(false)
    }
  }

  const openDeleteFinanceCategory = (category) => {
    setDeletingFinanceCategory(category)
    setShowDeleteFinanceDialog(true)
  }

  const handleDeleteFinanceCategory = async () => {
    if (!deletingFinanceCategory) return

    try {
      await axios.delete(`${API}/finance/categories/${deletingFinanceCategory.id}`)
      toast.success('Categoría eliminada')
      setShowDeleteFinanceDialog(false)
      fetchFinanceCategories()
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al eliminar'
      toast.error(message)
    }
  }

  const filteredFinanceCategories = financeCategories.filter(cat => {
    if (financeFilter === 'all') return cat.is_active
    return cat.is_active && cat.category_type === financeFilter
  })

  // Budget State
  const [budgets, setBudgets] = useState([])
  const [loadingBudgets, setLoadingBudgets] = useState(false)
  const [showBudgetModal, setShowBudgetModal] = useState(false)
  const [editingBudget, setEditingBudget] = useState(null)
  const [selectedCategoryForBudget, setSelectedCategoryForBudget] = useState(null)
  const [budgetForm, setBudgetForm] = useState({
    monto_mensual: '',
    alerta_80: true,
    alerta_100: true
  })
  const [savingBudget, setSavingBudget] = useState(false)

  // Fetch budgets
  const fetchBudgets = useCallback(async () => {
    setLoadingBudgets(true)
    try {
      const response = await axios.get(`${API}/finance/budgets`)
      setBudgets(response.data)
    } catch (error) {
      console.error('Error fetching budgets:', error)
    } finally {
      setLoadingBudgets(false)
    }
  }, [])

  useEffect(() => {
    fetchBudgets()
  }, [fetchBudgets])

  // Open budget modal for a category
  const openBudgetModal = (category) => {
    setSelectedCategoryForBudget(category)
    const existingBudget = budgets.find(b => b.category_id === category.id)
    if (existingBudget) {
      setEditingBudget(existingBudget)
      setBudgetForm({
        monto_mensual: existingBudget.monto_mensual.toString(),
        alerta_80: existingBudget.alerta_80,
        alerta_100: existingBudget.alerta_100
      })
    } else {
      setEditingBudget(null)
      setBudgetForm({
        monto_mensual: '',
        alerta_80: true,
        alerta_100: true
      })
    }
    setShowBudgetModal(true)
  }

  // Save budget
  const handleSaveBudget = async () => {
    if (!budgetForm.monto_mensual || parseFloat(budgetForm.monto_mensual) <= 0) {
      toast.error('Ingresa un monto válido')
      return
    }

    if (savingBudget) return
    setSavingBudget(true)

    try {
      const payload = {
        monto_mensual: parseFloat(budgetForm.monto_mensual),
        alerta_80: budgetForm.alerta_80,
        alerta_100: budgetForm.alerta_100
      }

      if (editingBudget) {
        await axios.put(`${API}/finance/budgets/${editingBudget.id}`, payload)
        toast.success('Presupuesto actualizado')
      } else {
        await axios.post(`${API}/finance/budgets`, {
          ...payload,
          category_id: selectedCategoryForBudget.id,
          moneda: 'MXN'
        })
        toast.success('Presupuesto creado')
      }
      setShowBudgetModal(false)
      fetchBudgets()
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al guardar'
      toast.error(message)
    } finally {
      setSavingBudget(false)
    }
  }

  // Delete budget
  const handleDeleteBudget = async () => {
    if (!editingBudget) return
    
    try {
      await axios.delete(`${API}/finance/budgets/${editingBudget.id}`)
      toast.success('Presupuesto eliminado')
      setShowBudgetModal(false)
      fetchBudgets()
    } catch (error) {
      toast.error('Error al eliminar presupuesto')
    }
  }

  // Get budget for a category
  const getBudgetForCategory = (categoryId) => {
    return budgets.find(b => b.category_id === categoryId)
  }

  // Mejora Diaria Subcategories State
  const [showSubcategoriesModal, setShowSubcategoriesModal] = useState(false)
  const [selectedCategoryForSubcats, setSelectedCategoryForSubcats] = useState(null)
  const [subcategories, setSubcategories] = useState([])
  const [loadingSubcategories, setLoadingSubcategories] = useState(false)
  const [showSubcategoryForm, setShowSubcategoryForm] = useState(false)
  const [editingSubcategory, setEditingSubcategory] = useState(null)
  const [subcategoryForm, setSubcategoryForm] = useState({
    name: '',
    points_per_log: ''
  })
  const [savingSubcategory, setSavingSubcategory] = useState(false)

  // Fetch subcategories for a category
  const fetchSubcategories = useCallback(async (categoryId) => {
    setLoadingSubcategories(true)
    try {
      const response = await axios.get(`${API}/mejora-subcategories`, {
        params: { category_id: categoryId, include_inactive: true }
      })
      setSubcategories(response.data)
    } catch (error) {
      console.error('Error fetching subcategories:', error)
      toast.error('Error al cargar subcategorías')
    } finally {
      setLoadingSubcategories(false)
    }
  }, [])

  // Open subcategories modal for a category
  const openSubcategoriesModal = (category) => {
    setSelectedCategoryForSubcats(category)
    setShowSubcategoriesModal(true)
    fetchSubcategories(category.id)
  }

  // Open form to create new subcategory
  const openNewSubcategory = () => {
    setEditingSubcategory(null)
    setSubcategoryForm({ name: '', points_per_log: '' })
    setShowSubcategoryForm(true)
  }

  // Open form to edit subcategory
  const openEditSubcategory = (subcat) => {
    setEditingSubcategory(subcat)
    setSubcategoryForm({
      name: subcat.name,
      points_per_log: subcat.points_per_log?.toString() || ''
    })
    setShowSubcategoryForm(true)
  }

  // Save subcategory (create or update)
  const handleSaveSubcategory = async () => {
    if (!subcategoryForm.name.trim()) {
      toast.error('El nombre es obligatorio')
      return
    }

    if (savingSubcategory) return
    setSavingSubcategory(true)

    try {
      const payload = {
        name: subcategoryForm.name.trim(),
        points_per_log: subcategoryForm.points_per_log ? parseInt(subcategoryForm.points_per_log) : null
      }

      if (editingSubcategory) {
        await axios.put(`${API}/mejora-subcategories/${editingSubcategory.id}`, payload)
        toast.success('Subcategoría actualizada')
      } else {
        await axios.post(`${API}/mejora-subcategories`, {
          ...payload,
          category_id: selectedCategoryForSubcats.id
        })
        toast.success('Subcategoría creada')
      }
      setShowSubcategoryForm(false)
      fetchSubcategories(selectedCategoryForSubcats.id)
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al guardar'
      toast.error(message)
    } finally {
      setSavingSubcategory(false)
    }
  }

  // Toggle subcategory active status
  const toggleSubcategoryActive = async (subcat) => {
    try {
      await axios.put(`${API}/mejora-subcategories/${subcat.id}`, {
        is_active: !subcat.is_active
      })
      toast.success(subcat.is_active ? 'Subcategoría desactivada' : 'Subcategoría activada')
      fetchSubcategories(selectedCategoryForSubcats.id)
    } catch (error) {
      toast.error('Error al actualizar')
    }
  }

  // Delete subcategory
  const handleDeleteSubcategory = async (subcat) => {
    try {
      await axios.delete(`${API}/mejora-subcategories/${subcat.id}`)
      toast.success('Subcategoría eliminada')
      fetchSubcategories(selectedCategoryForSubcats.id)
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al eliminar'
      toast.error(message)
    }
  }

  // ==== FINANCE SUBCATEGORIES FUNCTIONS ====
  const fetchFinanceSubcategories = useCallback(async (categoryId) => {
    try {
      const response = await axios.get(`${API}/finance/subcategories`, {
        params: { category_id: categoryId, include_inactive: true }
      })
      setFinanceSubcategories(response.data)
    } catch (error) {
      console.error('Error fetching finance subcategories:', error)
      toast.error('Error al cargar subcategorías')
    }
  }, [])

  const openFinanceSubcategoriesModal = (category) => {
    setSelectedFinanceCategory(category)
    setShowFinanceSubcategoriesModal(true)
    fetchFinanceSubcategories(category.id)
  }

  const openNewFinanceSubcategory = () => {
    setEditingFinanceSubcategory(null)
    setFinanceSubcategoryForm({ name: '' })
    setShowFinanceSubcategoryForm(true)
  }

  const openEditFinanceSubcategory = (subcat) => {
    setEditingFinanceSubcategory(subcat)
    setFinanceSubcategoryForm({ name: subcat.name })
    setShowFinanceSubcategoryForm(true)
  }

  const handleSaveFinanceSubcategory = async () => {
    if (!financeSubcategoryForm.name.trim()) {
      toast.error('El nombre es obligatorio')
      return
    }

    try {
      const payload = { name: financeSubcategoryForm.name.trim() }

      if (editingFinanceSubcategory) {
        await axios.put(`${API}/finance/subcategories/${editingFinanceSubcategory.id}`, payload)
        toast.success('Subcategoría actualizada')
      } else {
        await axios.post(`${API}/finance/subcategories`, {
          ...payload,
          category_id: selectedFinanceCategory.id
        })
        toast.success('Subcategoría creada')
      }
      setShowFinanceSubcategoryForm(false)
      fetchFinanceSubcategories(selectedFinanceCategory.id)
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al guardar'
      toast.error(message)
    }
  }

  const toggleFinanceSubcategoryActive = async (subcat) => {
    try {
      await axios.put(`${API}/finance/subcategories/${subcat.id}`, {
        is_active: !subcat.is_active
      })
      toast.success(subcat.is_active ? 'Subcategoría desactivada' : 'Subcategoría activada')
      fetchFinanceSubcategories(selectedFinanceCategory.id)
    } catch (error) {
      toast.error('Error al actualizar')
    }
  }

  const handleDeleteFinanceSubcategory = async (subcat) => {
    try {
      await axios.delete(`${API}/finance/subcategories/${subcat.id}`)
      toast.success('Subcategoría eliminada')
      fetchFinanceSubcategories(selectedFinanceCategory.id)
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al eliminar'
      toast.error(message)
    }
  }

  const handleSaveGoal = async () => {
    setSaving(true)
    try {
      await updateSettings({ daily_goal: dailyGoal, theme })
      toast.success('Configuración guardada')
    } catch (error) {
      toast.error('Error al guardar')
    } finally {
      setSaving(false)
    }
  }

  const isDarkMode = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches)

  const handleThemeToggle = () => {
    setThemeMode(isDarkMode ? 'light' : 'dark')
  }

  const openNewCategory = () => {
    setEditingCategory(null)
    setCategoryForm({ name: '', icon: 'Circle', color: '#6b7280' })
    setShowCategoryModal(true)
  }

  const openEditCategory = (category) => {
    setEditingCategory(category)
    setCategoryForm({
      name: category.name,
      icon: category.icon,
      color: category.color
    })
    setShowCategoryModal(true)
  }

  const handleSaveCategory = async () => {
    if (!categoryForm.name.trim()) {
      toast.error('El nombre es obligatorio')
      return
    }

    if (savingCategory) return // Prevent double submit
    setSavingCategory(true)

    try {
      if (editingCategory) {
        await updateCategory(editingCategory.id, categoryForm)
        toast.success('Categoría actualizada')
      } else {
        await addCategory(categoryForm)
        toast.success('Categoría creada')
      }
      setShowCategoryModal(false)
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al guardar la categoría'
      toast.error(message)
      console.error('Error saving category:', error)
    } finally {
      setSavingCategory(false)
    }
  }

  const handleMoveCategory = async (categoryId, direction) => {
    const currentIndex = activeCategories.findIndex(c => c.id === categoryId)
    if (
      (direction === 'up' && currentIndex === 0) ||
      (direction === 'down' && currentIndex === activeCategories.length - 1)
    ) {
      return
    }

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1
    const newOrder = [...activeCategories]
    const [moved] = newOrder.splice(currentIndex, 1)
    newOrder.splice(newIndex, 0, moved)

    try {
      await reorderCategories(newOrder.map(c => c.id))
      toast.success('Orden actualizado')
    } catch (error) {
      toast.error('Error al reordenar')
    }
  }

  const openDeleteDialog = (category) => {
    setDeletingCategory(category)
    setDeleteAction('archive')
    setReassignTo('')
    setShowDeleteDialog(true)
  }

  const handleDeleteCategory = async () => {
    if (!deletingCategory) return

    try {
      await deleteCategory(deletingCategory.id, deleteAction, reassignTo || undefined)
      
      if (deleteAction === 'archive') {
        toast.success('Categoría archivada')
      } else if (deleteAction === 'reassign') {
        toast.success('Historial reasignado y categoría eliminada')
      } else {
        toast.success('Categoría eliminada permanentemente')
      }
      
      setShowDeleteDialog(false)
      fetchCategories(showArchived)
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al eliminar'
      toast.error(message)
    }
  }

  const handleRestoreCategory = async (categoryId) => {
    try {
      await restoreCategory(categoryId)
      toast.success('Categoría restaurada')
    } catch (error) {
      toast.error('Error al restaurar')
    }
  }

  const SelectedIcon = iconMap[categoryForm.icon] || Circle

  return (
    <div className="p-4 md:p-8 pb-safe max-w-2xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold mb-1">{t('settings.title')}</h1>
        <p className="text-muted-foreground text-sm">{locale === 'es-MX' ? 'Personaliza tu experiencia' : 'Customize your experience'}</p>
      </div>

      {/* User Info */}
      <Card className="mb-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <User className="w-4 h-4" />
            {t('settings.profile')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <p className="font-medium">{user?.name}</p>
            <p className="text-sm text-muted-foreground">{user?.email}</p>
          </div>
        </CardContent>
      </Card>

      {/* Language Selection */}
      <Card className="mb-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <Globe className="w-4 h-4" />
            {t('settings.language')}
          </CardTitle>
          <CardDescription>
            {t('settings.languageDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3">
            <button
              onClick={() => setLocale('es-MX')}
              className={`flex items-center gap-2 px-4 py-3 rounded-xl border-2 transition-all ${
                locale === 'es-MX'
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/50'
              }`}
              data-testid="settings-lang-es"
            >
              <span className="text-xl">🇲🇽</span>
              <span className="font-medium">{t('settings.spanish')}</span>
              {locale === 'es-MX' && <Check className="w-4 h-4 text-primary" />}
            </button>
            <button
              onClick={() => setLocale('en-US')}
              className={`flex items-center gap-2 px-4 py-3 rounded-xl border-2 transition-all ${
                locale === 'en-US'
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/50'
              }`}
              data-testid="settings-lang-en"
            >
              <span className="text-xl">🇺🇸</span>
              <span className="font-medium">{t('settings.english')}</span>
              {locale === 'en-US' && <Check className="w-4 h-4 text-primary" />}
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Daily Goal */}
      <Card className="mb-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <Target className="w-4 h-4" />
            {t('settings.dailyGoal')}
          </CardTitle>
          <CardDescription>
            {t('settings.dailyGoalDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <Input
              type="number"
              min={1}
              max={24}
              value={dailyGoal}
              onChange={(e) => setDailyGoal(parseInt(e.target.value) || 2)}
              className="w-24 text-center text-lg font-bold"
              data-testid="daily-goal-input"
            />
            <span className="text-muted-foreground">{locale === 'es-MX' ? 'horas' : 'hours'}</span>
            <Button 
              onClick={handleSaveGoal} 
              disabled={saving}
              className="ml-auto rounded-full"
              data-testid="save-goal-btn"
            >
              {saving ? t('settings.saving') : t('common.save')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Theme */}
      <Card className="mb-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            {isDarkMode ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
            {t('settings.theme')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Sun className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm">{locale === 'es-MX' ? 'Modo oscuro' : 'Dark mode'}</span>
              <Moon className="w-4 h-4 text-muted-foreground" />
            </div>
            <Switch
              checked={isDarkMode}
              onCheckedChange={handleThemeToggle}
              data-testid="theme-toggle"
            />
          </div>
        </CardContent>
      </Card>

      {/* Categories Management */}
      <Card className="mb-6">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base font-medium flex items-center gap-2">
                <FolderOpen className="w-4 h-4" />
                {t('settings.categories')}
              </CardTitle>
              <CardDescription>
                {t('settings.categoriesDescription')}
              </CardDescription>
            </div>
            <Button 
              onClick={openNewCategory} 
              size="sm" 
              className="rounded-full"
              data-testid="add-category-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              {locale === 'es-MX' ? 'Nueva' : 'New'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Active Categories */}
          {activeCategories.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              {locale === 'es-MX' 
                ? 'No tienes categorías. Crea una para empezar a registrar tu progreso.'
                : 'No categories yet. Create one to start tracking your progress.'}
            </p>
          ) : (
            <div className="space-y-2 mb-4">
              {activeCategories.map((category, index) => {
                const Icon = iconMap[category.icon] || Circle
                return (
                  <div 
                    key={category.id}
                    className="flex items-center gap-3 p-3 rounded-xl bg-secondary/50"
                    data-testid={`category-item-${category.id}`}
                  >
                    <div 
                      className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: category.color }}
                    >
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="font-medium truncate block">{category.name}</span>
                      <button
                        onClick={() => openSubcategoriesModal(category)}
                        className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 mt-0.5"
                        data-testid={`subcategories-btn-${category.id}`}
                      >
                        <Layers className="w-3 h-3" />
                        {t('settings.subcategories')}
                        <ChevronRight className="w-3 h-3" />
                      </button>
                    </div>
                    
                    {/* Action buttons - always visible */}
                    <div className="flex items-center gap-0.5 flex-shrink-0">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleMoveCategory(category.id, 'up')}
                        disabled={index === 0}
                        data-testid={`move-up-${category.id}`}
                      >
                        <ChevronUp className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleMoveCategory(category.id, 'down')}
                        disabled={index === activeCategories.length - 1}
                        data-testid={`move-down-${category.id}`}
                      >
                        <ChevronDown className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => openEditCategory(category)}
                        data-testid={`edit-category-${category.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => openDeleteDialog(category)}
                        data-testid={`delete-category-${category.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {/* Archived toggle */}
          {archivedCategories.length > 0 && (
            <div className="border-t pt-4">
              <button
                onClick={() => setShowArchived(!showArchived)}
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <Archive className="w-4 h-4" />
                {showArchived ? 'Ocultar' : 'Mostrar'} archivadas ({archivedCategories.length})
              </button>
              
              {showArchived && (
                <div className="space-y-2 mt-3">
                  {archivedCategories.map((category) => {
                    const Icon = iconMap[category.icon] || Circle
                    return (
                      <div 
                        key={category.id}
                        className="flex items-center gap-3 p-3 rounded-xl bg-secondary/30 opacity-60"
                      >
                        <div 
                          className="w-10 h-10 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: category.color }}
                        >
                          <Icon className="w-5 h-5 text-white" />
                        </div>
                        <span className="font-medium flex-1 line-through">{category.name}</span>
                        <Badge variant="outline" className="text-xs">Archivada</Badge>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRestoreCategory(category.id)}
                          data-testid={`restore-category-${category.id}`}
                        >
                          <RotateCcw className="w-4 h-4 mr-1" />
                          Restaurar
                        </Button>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Finance Categories Management */}
      <Card className="mb-6">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base font-medium flex items-center gap-2">
                <Wallet className="w-4 h-4" />
                Categorías Finanzas
              </CardTitle>
              <CardDescription>
                Gestiona tus categorías de ingresos y gastos
              </CardDescription>
            </div>
            <Button 
              onClick={openNewFinanceCategory} 
              size="sm" 
              className="rounded-full"
              data-testid="add-finance-category-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Nueva
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filter tabs */}
          <div className="flex gap-2 mb-4">
            <Button
              variant={financeFilter === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFinanceFilter('all')}
              className="rounded-full"
            >
              Todas
            </Button>
            <Button
              variant={financeFilter === 'expense' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFinanceFilter('expense')}
              className="rounded-full"
            >
              <ArrowDownCircle className="w-4 h-4 mr-1" /> Gastos
            </Button>
            <Button
              variant={financeFilter === 'income' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFinanceFilter('income')}
              className="rounded-full"
            >
              <ArrowUpCircle className="w-4 h-4 mr-1" /> Ingresos
            </Button>
          </div>

          {loadingFinance ? (
            <p className="text-sm text-muted-foreground text-center py-4">Cargando...</p>
          ) : filteredFinanceCategories.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              No tienes categorías de finanzas. Crea una para empezar.
            </p>
          ) : (
            <div className="space-y-2">
              {filteredFinanceCategories.map((category) => {
                const Icon = iconMap[category.icon] || Circle
                const budget = getBudgetForCategory(category.id)
                const isExpense = category.category_type !== 'income'
                
                return (
                  <div 
                    key={category.id}
                    className="flex items-center gap-3 p-3 rounded-xl bg-secondary/50"
                    data-testid={`finance-category-item-${category.id}`}
                  >
                    <div 
                      className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${category.color}20` }}
                    >
                      <Icon className="w-5 h-5" style={{ color: category.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="font-medium truncate block">{category.name}</span>
                      <button
                        onClick={() => openFinanceSubcategoriesModal(category)}
                        className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 mt-0.5"
                        data-testid={`finance-subcategories-btn-${category.id}`}
                      >
                        <Layers className="w-3 h-3" />
                        Subcategorías
                        <ChevronRight className="w-3 h-3" />
                      </button>
                      {isExpense && budget ? (
                        <div className="mt-1">
                          <div className="flex items-center justify-between text-xs mb-1">
                            <span className="text-muted-foreground">
                              ${budget.gastado.toLocaleString()} / ${budget.monto_mensual.toLocaleString()}
                            </span>
                            <span className={`font-medium ${
                              budget.porcentaje_usado >= 100 ? 'text-red-500' : 
                              budget.porcentaje_usado >= 80 ? 'text-yellow-500' : 
                              'text-emerald-500'
                            }`}>
                              {budget.porcentaje_usado}%
                            </span>
                          </div>
                          <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
                            <div 
                              className={`h-full rounded-full transition-all ${
                                budget.porcentaje_usado >= 100 ? 'bg-red-500' : 
                                budget.porcentaje_usado >= 80 ? 'bg-yellow-500' : 
                                'bg-emerald-500'
                              }`}
                              style={{ width: `${Math.min(budget.porcentaje_usado, 100)}%` }}
                            />
                          </div>
                        </div>
                      ) : isExpense ? (
                        <button
                          onClick={() => openBudgetModal(category)}
                          className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 mt-0.5"
                          data-testid={`set-budget-${category.id}`}
                        >
                          <DollarSign className="w-3 h-3" />
                          Definir presupuesto
                        </button>
                      ) : null}
                    </div>
                    
                    <div className="flex items-center gap-0.5 flex-shrink-0">
                      {isExpense && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => openBudgetModal(category)}
                          title={budget ? 'Editar presupuesto' : 'Definir presupuesto'}
                          data-testid={`budget-btn-${category.id}`}
                        >
                          <DollarSign className={`w-4 h-4 ${budget ? 'text-emerald-500' : 'text-muted-foreground'}`} />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => openEditFinanceCategory(category)}
                        data-testid={`edit-finance-category-${category.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => openDeleteFinanceCategory(category)}
                        data-testid={`delete-finance-category-${category.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Idea Buckets Management */}
      <Card className="mb-6">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base font-medium flex items-center gap-2">
                <Lightbulb className="w-4 h-4 text-amber-500" />
                {t('settings.ideaBuckets')}
              </CardTitle>
              <CardDescription>
                {t('settings.ideaBucketsDescription')}
              </CardDescription>
            </div>
            <Button 
              onClick={openNewBucket} 
              size="sm" 
              className="rounded-full"
              data-testid="add-bucket-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              {locale === 'es-MX' ? 'Nueva' : 'New'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loadingBuckets ? (
            <div className="text-center py-4 text-muted-foreground">
              <div className="animate-pulse">{t('common.loading')}</div>
            </div>
          ) : ideaBuckets.filter(b => b.is_active).length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              {t('settings.noBuckets')}
            </p>
          ) : (
            <div className="space-y-2">
              {ideaBuckets.filter(b => b.is_active).map((bucket) => {
                const BucketIcon = iconMap[bucket.icon] || Circle
                return (
                  <div 
                    key={bucket.id}
                    className="flex items-center gap-3 p-3 rounded-xl bg-secondary/50"
                    data-testid={`bucket-item-${bucket.id}`}
                  >
                    <div 
                      className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 bg-amber-500/20"
                    >
                      <BucketIcon className="w-5 h-5 text-amber-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="font-medium truncate block">{bucket.name}</span>
                    </div>
                    
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => openEditBucket(bucket)}
                        data-testid={`edit-bucket-${bucket.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => openDeleteBucket(bucket)}
                        data-testid={`delete-bucket-${bucket.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Logout */}
      <Button
        variant="outline"
        className="w-full h-12 text-destructive hover:text-destructive rounded-full"
        onClick={logout}
        data-testid="logout-btn"
      >
        <LogOut className="w-4 h-4 mr-2" />
        {t('settings.logout')}
      </Button>

      {/* Finance Category Modal - Using Reusable Component */}
      <FinanceCategoryModal
        open={showFinanceCategoryModal}
        onOpenChange={setShowFinanceCategoryModal}
        editingCategory={editingFinanceCategory}
        onSuccess={() => {
          setShowFinanceCategoryModal(false)
          setEditingFinanceCategory(null)
          fetchFinanceCategories()
        }}
      />

      {/* Delete Finance Category Confirmation */}
      <AlertDialog open={showDeleteFinanceDialog} onOpenChange={setShowDeleteFinanceDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              ¿Eliminar "{deletingFinanceCategory?.name}"?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Esta categoría será desactivada y no aparecerá en el selector de transacciones.
              Las transacciones existentes mantendrán su categoría.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteFinanceCategory}
              className="bg-destructive hover:bg-destructive/90"
              data-testid="confirm-delete-finance-category-btn"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Category Modal - Using Reusable Component */}
      <MejoraCategoryModal
        open={showCategoryModal}
        onOpenChange={setShowCategoryModal}
        editingCategory={editingCategory}
        onSuccess={() => {
          setShowCategoryModal(false)
          setEditingCategory(null)
          fetchCategories()
        }}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Eliminar "{deletingCategory?.name}"
            </AlertDialogTitle>
            <AlertDialogDescription>
              ¿Qué deseas hacer con esta categoría y su historial?
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-3">
              <label className="flex items-start gap-3 p-3 rounded-lg border cursor-pointer hover:bg-secondary/50">
                <input
                  type="radio"
                  name="deleteAction"
                  value="archive"
                  checked={deleteAction === 'archive'}
                  onChange={(e) => setDeleteAction(e.target.value)}
                  className="mt-1"
                />
                <div>
                  <p className="font-medium">Archivar (recomendado)</p>
                  <p className="text-sm text-muted-foreground">
                    Oculta la categoría pero mantiene todo el historial
                  </p>
                </div>
              </label>
              
              <label className="flex items-start gap-3 p-3 rounded-lg border cursor-pointer hover:bg-secondary/50">
                <input
                  type="radio"
                  name="deleteAction"
                  value="reassign"
                  checked={deleteAction === 'reassign'}
                  onChange={(e) => setDeleteAction(e.target.value)}
                  className="mt-1"
                />
                <div className="flex-1">
                  <p className="font-medium">Reasignar historial</p>
                  <p className="text-sm text-muted-foreground mb-2">
                    Mueve los registros a otra categoría y elimina esta
                  </p>
                  {deleteAction === 'reassign' && (
                    <Select value={reassignTo} onValueChange={setReassignTo}>
                      <SelectTrigger className="mt-2">
                        <SelectValue placeholder="Selecciona categoría destino" />
                      </SelectTrigger>
                      <SelectContent>
                        {activeCategories
                          .filter(c => c.id !== deletingCategory?.id)
                          .map((cat) => (
                            <SelectItem key={cat.id} value={cat.id}>
                              {cat.name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </label>
              
              <label className="flex items-start gap-3 p-3 rounded-lg border border-destructive/50 cursor-pointer hover:bg-destructive/10">
                <input
                  type="radio"
                  name="deleteAction"
                  value="delete"
                  checked={deleteAction === 'delete'}
                  onChange={(e) => setDeleteAction(e.target.value)}
                  className="mt-1"
                />
                <div>
                  <p className="font-medium text-destructive">Eliminar permanentemente</p>
                  <p className="text-sm text-muted-foreground">
                    Elimina la categoría y todo su historial. Esta acción es irreversible.
                  </p>
                </div>
              </label>
            </div>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteCategory}
              disabled={deleteAction === 'reassign' && !reassignTo}
              className={deleteAction === 'delete' ? 'bg-destructive hover:bg-destructive/90' : ''}
              data-testid="confirm-delete-btn"
            >
              {deleteAction === 'archive' && 'Archivar'}
              {deleteAction === 'reassign' && 'Reasignar y eliminar'}
              {deleteAction === 'delete' && 'Eliminar permanentemente'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Subcategories Modal */}
      <Dialog open={showSubcategoriesModal} onOpenChange={(open) => {
        setShowSubcategoriesModal(open)
        if (!open) {
          setShowSubcategoryForm(false)
          setEditingSubcategory(null)
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Layers className="w-5 h-5" />
              Subcategorías - {selectedCategoryForSubcats?.name}
            </DialogTitle>
            <DialogDescription>
              Organiza actividades específicas dentro de esta categoría
            </DialogDescription>
          </DialogHeader>

          {/* Subcategory Form */}
          {showSubcategoryForm ? (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Nombre *</Label>
                <Input
                  value={subcategoryForm.name}
                  onChange={(e) => setSubcategoryForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Ej: Pesas, Cardio, Yoga..."
                  data-testid="subcategory-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{locale === 'es-MX' ? 'Minutos por defecto (opcional)' : 'Default minutes (optional)'}</Label>
                <Input
                  type="number"
                  min={0}
                  max={480}
                  value={subcategoryForm.points_per_log}
                  onChange={(e) => setSubcategoryForm(prev => ({ ...prev, points_per_log: e.target.value }))}
                  placeholder={locale === 'es-MX' ? '15-120 (minutos sugeridos)' : '15-120 (suggested minutes)'}
                  data-testid="subcategory-points-input"
                />
                <p className="text-xs text-muted-foreground">
                  {locale === 'es-MX' 
                    ? 'Si defines un valor, se sugerirá automáticamente al registrar esta actividad'
                    : 'If you set a value, it will be suggested when logging this activity'}
                </p>
              </div>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setShowSubcategoryForm(false)} disabled={savingSubcategory}>
                  Cancelar
                </Button>
                <Button onClick={handleSaveSubcategory} disabled={savingSubcategory} data-testid="save-subcategory-btn">
                  {savingSubcategory ? 'Guardando...' : (editingSubcategory ? 'Guardar' : 'Crear')}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="py-4">
              {/* Add button */}
              <Button 
                onClick={openNewSubcategory} 
                size="sm" 
                className="w-full mb-4 rounded-full"
                data-testid="add-subcategory-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                Nueva subcategoría
              </Button>

              {/* Loading state */}
              {loadingSubcategories ? (
                <div className="text-center py-8 text-muted-foreground">
                  <div className="animate-pulse">Cargando...</div>
                </div>
              ) : subcategories.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Layers className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No hay subcategorías</p>
                  <p className="text-xs mt-1">Crea una para organizar mejor tus registros</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-72 overflow-y-auto">
                  {subcategories.map((subcat) => (
                    <div
                      key={subcat.id}
                      className={`flex items-center gap-3 p-3 rounded-xl bg-secondary/50 ${!subcat.is_active ? 'opacity-50' : ''}`}
                      data-testid={`subcategory-item-${subcat.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <span className={`font-medium truncate block ${!subcat.is_active ? 'line-through' : ''}`}>
                          {subcat.name}
                        </span>
                        {subcat.points_per_log !== null && subcat.points_per_log > 0 && (
                          <span className="text-xs text-muted-foreground">
                            {subcat.points_per_log} min {locale === 'es-MX' ? 'por defecto' : 'default'}
                          </span>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => toggleSubcategoryActive(subcat)}
                          title={subcat.is_active ? 'Desactivar' : 'Activar'}
                          data-testid={`toggle-subcategory-${subcat.id}`}
                        >
                          {subcat.is_active ? (
                            <ToggleRight className="w-4 h-4 text-emerald-500" />
                          ) : (
                            <ToggleLeft className="w-4 h-4 text-muted-foreground" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => openEditSubcategory(subcat)}
                          data-testid={`edit-subcategory-${subcat.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => handleDeleteSubcategory(subcat)}
                          data-testid={`delete-subcategory-${subcat.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Finance Subcategories Modal */}
      <Dialog open={showFinanceSubcategoriesModal} onOpenChange={(open) => {
        setShowFinanceSubcategoriesModal(open)
        if (!open) {
          setShowFinanceSubcategoryForm(false)
          setEditingFinanceSubcategory(null)
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Layers className="w-5 h-5" />
              Subcategorías - {selectedFinanceCategory?.name}
            </DialogTitle>
            <DialogDescription>
              Organiza gastos específicos dentro de esta categoría
            </DialogDescription>
          </DialogHeader>

          {/* Finance Subcategory Form */}
          {showFinanceSubcategoryForm ? (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Nombre *</Label>
                <Input
                  value={financeSubcategoryForm.name}
                  onChange={(e) => setFinanceSubcategoryForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Ej: Renta, Luz, Agua, Internet..."
                  data-testid="finance-subcategory-name-input"
                />
              </div>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setShowFinanceSubcategoryForm(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleSaveFinanceSubcategory} data-testid="save-finance-subcategory-btn">
                  {editingFinanceSubcategory ? 'Guardar' : 'Crear'}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="py-4">
              {/* Add button */}
              <Button 
                onClick={openNewFinanceSubcategory} 
                size="sm" 
                className="w-full mb-4 rounded-full"
                data-testid="add-finance-subcategory-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                Nueva subcategoría
              </Button>

              {/* Empty state */}
              {financeSubcategories.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Layers className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No hay subcategorías</p>
                  <p className="text-xs mt-1">Crea una para organizar mejor tus gastos</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-72 overflow-y-auto">
                  {financeSubcategories.map((subcat) => (
                    <div
                      key={subcat.id}
                      className={`flex items-center gap-3 p-3 rounded-xl bg-secondary/50 ${!subcat.is_active ? 'opacity-50' : ''}`}
                      data-testid={`finance-subcategory-item-${subcat.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <span className={`font-medium truncate block ${!subcat.is_active ? 'line-through' : ''}`}>
                          {subcat.name}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => toggleFinanceSubcategoryActive(subcat)}
                          title={subcat.is_active ? 'Desactivar' : 'Activar'}
                          data-testid={`toggle-finance-subcategory-${subcat.id}`}
                        >
                          {subcat.is_active ? (
                            <ToggleRight className="w-4 h-4 text-emerald-500" />
                          ) : (
                            <ToggleLeft className="w-4 h-4 text-muted-foreground" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => openEditFinanceSubcategory(subcat)}
                          data-testid={`edit-finance-subcategory-${subcat.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => handleDeleteFinanceSubcategory(subcat)}
                          data-testid={`delete-finance-subcategory-${subcat.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Budget Modal */}
      <Dialog open={showBudgetModal} onOpenChange={setShowBudgetModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              Presupuesto - {selectedCategoryForBudget?.name}
            </DialogTitle>
            <DialogDescription>
              Define un límite mensual de gasto para esta categoría
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Monto mensual (MXN) *</Label>
              <Input
                type="number"
                min={0}
                step={100}
                value={budgetForm.monto_mensual}
                onChange={(e) => setBudgetForm(prev => ({ ...prev, monto_mensual: e.target.value }))}
                placeholder="Ej: 8000"
                data-testid="budget-amount-input"
              />
            </div>

            <div className="space-y-3">
              <Label>Alertas push</Label>
              <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/50">
                <div className="flex items-center gap-2">
                  <Bell className="w-4 h-4 text-yellow-500" />
                  <span className="text-sm">Alerta al 80%</span>
                </div>
                <Switch
                  checked={budgetForm.alerta_80}
                  onCheckedChange={(checked) => setBudgetForm(prev => ({ ...prev, alerta_80: checked }))}
                  data-testid="alert-80-switch"
                />
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/50">
                <div className="flex items-center gap-2">
                  <Bell className="w-4 h-4 text-red-500" />
                  <span className="text-sm">Alerta al 100%</span>
                </div>
                <Switch
                  checked={budgetForm.alerta_100}
                  onCheckedChange={(checked) => setBudgetForm(prev => ({ ...prev, alerta_100: checked }))}
                  data-testid="alert-100-switch"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Recibirás una notificación push cuando alcances estos umbrales
              </p>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            {editingBudget && (
              <Button
                variant="destructive"
                onClick={handleDeleteBudget}
                className="mr-auto"
                data-testid="delete-budget-btn"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Eliminar
              </Button>
            )}
            <Button variant="outline" onClick={() => setShowBudgetModal(false)} disabled={savingBudget}>
              Cancelar
            </Button>
            <Button onClick={handleSaveBudget} disabled={savingBudget} data-testid="save-budget-btn">
              {savingBudget ? 'Guardando...' : (editingBudget ? 'Guardar' : 'Crear presupuesto')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Idea Bucket Modal - Using Reusable Component */}
      <IdeaBucketModal
        open={showBucketModal}
        onOpenChange={setShowBucketModal}
        editingBucket={editingBucket}
        onSuccess={() => {
          setShowBucketModal(false)
          setEditingBucket(null)
          fetchBuckets()
        }}
      />

      {/* Delete Bucket Confirmation */}
      <AlertDialog open={showDeleteBucketDialog} onOpenChange={setShowDeleteBucketDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              {locale === 'es-MX' 
                ? `¿Eliminar "${deletingBucket?.name}"?`
                : `Delete "${deletingBucket?.name}"?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {locale === 'es-MX'
                ? 'Esta cubeta será desactivada. Las ideas existentes mantendrán su referencia.'
                : 'This bucket will be deactivated. Existing ideas will keep their reference.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{locale === 'es-MX' ? 'Cancelar' : 'Cancel'}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteBucket}
              className="bg-destructive hover:bg-destructive/90"
              data-testid="confirm-delete-bucket-btn"
            >
              {locale === 'es-MX' ? 'Eliminar' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
